import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import { api, ApiError, type ClubInfo } from "../lib/api";
import { useAbortableConfirm } from "../lib/useAbortableConfirm";

interface StaffDraft {
  full_name: string;
  email: string;
  password: string;
}

const emptyStaff: StaffDraft = { full_name: "", email: "", password: "" };

export default function SuperadminClubsPage() {
  const { token } = useAuth();
  const { askConfirm } = useAbortableConfirm([]);
  const [clubs, setClubs] = useState<ClubInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [newClub, setNewClub] = useState({ name: "", contact_email: "" });
  const [staffForms, setStaffForms] = useState<Record<number, StaffDraft>>({});
  const [editing, setEditing] = useState<{ id: number; name: string; contact_email: string } | null>(null);

  async function handleSaveEdit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !editing || !editing.name.trim()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await api.clubs.superadminUpdate(token, editing.id, {
        name: editing.name.trim(),
        contact_email: editing.contact_email.trim() || null,
      });
      setEditing(null);
      setMessage("Datos del club guardados.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo guardar el club.");
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive(club: ClubInfo) {
    if (!token) return;
    const suspend = club.is_active;
    const confirmed = await askConfirm(
      suspend
        ? `¿Suspender «${club.name}»? Su equipo ya no podrá entrar al panel del club ni sus socios reservar. No se borra nada.`
        : `¿Activar «${club.name}»? Su equipo y sus socios vuelven a tener acceso.`,
      { acceptLabel: suspend ? "Sí, suspender club" : "Sí, activar club", tone: suspend ? "danger" : "default" },
    );
    if (!confirmed) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await api.clubs.superadminUpdate(token, club.id, { is_active: !suspend });
      setMessage(suspend ? "Club suspendido." : "Club activado.");
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo cambiar el estado del club.");
    } finally {
      setSaving(false);
    }
  }

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const resp = await api.clubs.list(token);
      setClubs(resp.data.clubs);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "No pudimos cargar los clubes.",
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  function staffDraft(clubId: number): StaffDraft {
    return staffForms[clubId] ?? emptyStaff;
  }

  function setStaffField(clubId: number, field: keyof StaffDraft, value: string) {
    setStaffForms((prev) => ({
      ...prev,
      [clubId]: { ...staffDraft(clubId), [field]: value },
    }));
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || !newClub.name.trim()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await api.clubs.create(token, {
        name: newClub.name.trim(),
        contact_email: newClub.contact_email.trim() || null,
      });
      setNewClub({ name: "", contact_email: "" });
      setMessage("Club creado.");
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message || "No se pudo crear." : "Error de red.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleAssignStaff(clubId: number, event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    const form = staffDraft(clubId);
    if (!form.email || !form.password || !form.full_name) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await api.clubs.assignStaff(token, clubId, {
        new_user: {
          full_name: form.full_name.trim(),
          email: form.email.trim(),
          password: form.password,
        },
      });
      setStaffForms((prev) => ({ ...prev, [clubId]: { ...emptyStaff } }));
      setMessage("Usuario de club creado y asignado.");
      await load();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message || "No se pudo asignar."
          : "Error de red.",
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="card">
        <div className="card__header-row">
          <div>
            <p className="eyebrow">Superadmin</p>
            <h1>Clubes</h1>
            <p className="card__subtitle">
              Da de alta clubes y su personal. Los usuarios de club reciben acceso al
              panel de torneos.
            </p>
          </div>
          <Link className="btn btn--ghost" to="/superadmin">
            ← Volver
          </Link>
        </div>
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {message && <p className="form-message">{message}</p>}
      </section>

      <section className="card">
        <h2 className="card__title">Nuevo club</h2>
        <form className="form" onSubmit={handleCreate}>
          <label>
            Nombre
            <input
              type="text"
              required
              value={newClub.name}
              disabled={saving}
              onChange={(e) => setNewClub({ ...newClub, name: e.target.value })}
            />
          </label>
          <label>
            Email de contacto
            <input
              type="email"
              value={newClub.contact_email}
              disabled={saving}
              onChange={(e) =>
                setNewClub({ ...newClub, contact_email: e.target.value })
              }
            />
          </label>
          <button className="btn" type="submit" disabled={saving}>
            {saving ? "Creando…" : "Crear club"}
          </button>
        </form>
      </section>

      <section className="card">
        <h2 className="card__title">Clubes ({clubs.length})</h2>
        {loading ? (
          <p className="muted">Cargando…</p>
        ) : clubs.length === 0 ? (
          <p className="muted">Sin clubes todavía.</p>
        ) : (
          <ul className="communications-feed">
            {clubs.map((club) => (
              <li key={club.id} className="communications-feed__item">
                <div className="communications-feed__meta">
                  <span
                    className={`status-pill${club.is_active ? "" : " status-pill--dropped"}`}
                  >
                    {club.is_active ? "Activo" : "Suspendido"}
                  </span>
                  <span className="muted">
                    /{club.slug} · {club.staff_count ?? 0} usuario(s)
                  </span>
                </div>
                <strong className="communications-feed__title">{club.name}</strong>
                {club.contact_email && <span className="muted">{club.contact_email}</span>}
                <div className="admin-home-actions">
                  <button
                    type="button"
                    className="btn btn--compact btn--ghost"
                    disabled={saving}
                    onClick={() =>
                      setEditing({ id: club.id, name: club.name, contact_email: club.contact_email ?? "" })
                    }
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className={`btn btn--compact${club.is_active ? " btn--danger" : ""}`}
                    disabled={saving}
                    onClick={() => void handleToggleActive(club)}
                  >
                    {club.is_active ? "Suspender" : "Activar"}
                  </button>
                </div>
                {editing?.id === club.id && (
                  <form className="form" onSubmit={(e) => void handleSaveEdit(e)} aria-label={`Editar ${club.name}`}>
                    <label>
                      Nombre del club
                      <input
                        type="text"
                        required
                        value={editing.name}
                        disabled={saving}
                        onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                      />
                    </label>
                    <label>
                      Email de contacto
                      <input
                        type="email"
                        value={editing.contact_email}
                        disabled={saving}
                        onChange={(e) => setEditing({ ...editing, contact_email: e.target.value })}
                      />
                    </label>
                    <div className="admin-home-actions">
                      <button className="btn btn--compact" type="submit" disabled={saving}>
                        Guardar
                      </button>
                      <button className="btn btn--compact btn--ghost" type="button" onClick={() => setEditing(null)}>
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}
                <form
                  className="form"
                  onSubmit={(e) => void handleAssignStaff(club.id, e)}
                >
                  <label>
                    Nombre del usuario
                    <input
                      type="text"
                      required
                      value={staffDraft(club.id).full_name}
                      disabled={saving}
                      onChange={(e) => setStaffField(club.id, "full_name", e.target.value)}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      required
                      value={staffDraft(club.id).email}
                      disabled={saving}
                      onChange={(e) => setStaffField(club.id, "email", e.target.value)}
                    />
                  </label>
                  <label>
                    Contraseña
                    <input
                      type="password"
                      required
                      minLength={8}
                      autoComplete="new-password"
                      value={staffDraft(club.id).password}
                      disabled={saving}
                      onChange={(e) => setStaffField(club.id, "password", e.target.value)}
                    />
                    <span className="form__hint">Mínimo 8 caracteres.</span>
                  </label>
                  <button className="btn btn--compact" type="submit" disabled={saving}>
                    {saving ? "Creando usuario…" : "Crear usuario de club"}
                  </button>
                </form>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
